/**
 * Livom CH - Warehouse outlet shipping display override.
 *
 * Same safe model as the validated NL version:
 * - Does not change checkout.
 * - Does not change Shopify cart data.
 * - Does not submit forms.
 * - Does not patch product rows.
 * - Does not set textContent on parent containers.
 * - Only replaces the exact text node next to the shipping and total labels.
 *
 * CH specifics:
 * - Currency is CHF, not EUR. The money parser and the money detector below were
 *   generalized to handle Swiss formatting (apostrophe thousands, period decimal,
 *   e.g. "CHF 1'234.50") as well as EUR.
 * - Summary labels: German + English + French candidates are included, because the
 *   CH store may run any of those (French showrooms exist). Verify with debug().
 * - The total is computed with a discount-safe delta (see applyPatch), which also
 *   sidesteps any "Total"/"Subtotal" or "Summe"/"Zwischensumme" substring clash.
 *
 * TWO THINGS TO CONFIRM FOR CH (see SHIPPING_RULES / MONEY_* below):
 *   1) Rule amounts in CHF: warehouse (verify CH rate).
 *      Do NOT assume they match the EUR stores.
 *   2) The money output format (MONEY_* constants): match it to how the CH drawer
 *      renders amounts (currently "5,575.00 SFr.").
 *      The PARSER already tolerates other styles; only the OUTPUT needs to match.
 *
 * Triggers (see SHIPPING_RULES below):
 * - warehouse:  product_type = "Outlet" (warehouse) -> 250 CHF
 * First match wins per item; distinct matched rules across the cart are summed.
 *
 * BEFORE GOING LIVE:
 *   1) Open the drawer with a warehouse item and run
 *      LivomWarehouseShippingOverride.debug() in the console. Check summaryCount >= 1
 *      and that shipping/total valueText are found.
 *   2) Test a warehouse item WITH a discount code and compare the drawer total to
 *      checkout. The delta method keeps Dr Stacked's own discounted total, so it
 *      should match.
 *   3) Set DEBUG to false for production.
 */

(function () {
  "use strict";

  // The script tag lives in sections/kc-main-cart.liquid (cart page only). Guard against a
  // second evaluation if the section markup is ever re-inserted with its <script>.
  if (window.__livomOutletShippingInit) return;
  window.__livomOutletShippingInit = true;

  var DEBUG = false;

  // ---- Shipping rules (CH prices, in CHF) ---------------------------------
  // Same model as DE: each rule maps a product signal to a flat amount for THIS
  // store. All conditions set on a rule must pass (productType, tag,
  // titleIncludes; leave a condition null to ignore it). First match wins per
  // item; distinct matched rules across the cart are summed (mirrors one rate
  // per Shopify shipping profile). Prices are per-country: CH amounts here.
  var SHIPPING_RULES = [
    {
      key: "warehouse",
      amount: 250.0, // CONFIRM: CH warehouse profile rate in CHF
      productType: "outlet", // product_type "Outlet" = warehouse ("Outlet Showroom" excluded)
      tag: null, // product_type alone distinguishes warehouse now
      titleIncludes: null
    }
  ];

  // ---- Money format (matched to the live CH drawer: "5,575.00 SFr.") ------
  // The parser tolerates other styles; these only control the OUTPUT.
  var MONEY_SYMBOL = "SFr.";
  var MONEY_SYMBOL_BEFORE = false; // false -> "250.00 SFr." ; true -> "SFr. 250.00"
  var MONEY_SYMBOL_SPACE = true; // space between number and symbol
  var MONEY_THOUSANDS = ","; // this CH store uses comma thousands ("5,575")
  var MONEY_DECIMAL = "."; // period decimal

  // ---- Labels (German + English + French candidates) ----------------------
  var SHIPPING_LABELS = [
    "geschätzte versandkosten",
    "voraussichtliche versandkosten",
    "versandkosten",
    "versand",
    "frais de livraison",
    "frais d'expédition",
    "livraison",
    "expédition",
    "shipping"
  ];

  var TOTAL_LABELS = [
    "geschätzte gesamtsumme",
    "voraussichtliche gesamtsumme",
    "geschätztes gesamt",
    "gesamtsumme",
    "gesamtbetrag",
    "gesamt",
    "summe",
    "montant total",
    "estimated total",
    "total"
  ];

  var SUBTOTAL_LABELS = [
    "zwischensumme",
    "zwischen summe",
    "sous-total",
    "sous total",
    "subtotal",
    "subtotaal"
  ];

  var FREE_LABELS = [
    "gratis",
    "kostenlose",
    "kostenlos",
    "gratuite",
    "gratuit",
    "offerte",
    "offert",
    "free"
  ];

  var tagCache = {};
  var refreshTimer = null;
  var applyTimer = null;
  var domObserver = null;

  var lastMatchedKeys = [];
  var lastTargetShipping = null;
  var lastCartBaseTotal = null;

  var patchedTextNodes = [];

  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(
      console,
      ["[Livom CH warehouse shipping override]"].concat([].slice.call(arguments))
    );
  }

  function isCheckoutPage() {
    return /\/checkouts?\b|\/checkout\b/i.test(window.location.pathname);
  }

  function getShopifyRoot() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === "string"
    ) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  function shopifyUrl(path) {
    return getShopifyRoot().replace(/\/$/, "/") + String(path || "").replace(/^\//, "");
  }

  function fetchJson(url) {
    return fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json"
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("HTTP " + response.status + " for " + url);
      }

      return response.json();
    });
  }

  function cleanText(text) {
    return String(text || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function lowerText(text) {
    return cleanText(text).toLowerCase();
  }

  function elementText(element) {
    return cleanText((element && element.textContent) || "");
  }

  function containsAny(text, list) {
    var value = lowerText(text);

    for (var i = 0; i < list.length; i++) {
      if (value.indexOf(list[i]) !== -1) {
        return true;
      }
    }

    return false;
  }

  function isFreeText(text) {
    return containsAny(text, FREE_LABELS);
  }

  function looksLikeMoney(text) {
    var value = cleanText(text);

    // A number with optional thousands separators (. , or apostrophe) and an
    // optional 2-digit decimal (. or ,), with a currency token before or after.
    // The third alternative accepts a standalone number with 2 decimals, in case
    // the amount and the "SFr." token sit in separate text nodes.
    var number = "(?:\\d{1,3}(?:[.,'\\u2019\\u00b4]\\d{3})*|\\d+)(?:[.,]\\d{2})?";
    var numberWithDecimals = "(?:\\d{1,3}(?:[.,'\\u2019\\u00b4]\\d{3})*|\\d+)[.,]\\d{2}";
    var currency = "(?:€|eur|chf|sfr\\.?|fr\\.?)";
    var re = new RegExp(
      "(?:" + currency + "\\s*" + number + ")|(?:" + number + "\\s*" + currency + ")|(?:^" + numberWithDecimals + "$)",
      "i"
    );

    return re.test(value);
  }

  function parseMoney(text) {
    if (!text) return null;

    if (isFreeText(text)) {
      return 0;
    }

    // Drop currency tokens and whitespace, then apostrophe thousands separators.
    var s = String(text)
      .replace(/\u00a0/g, " ")
      .replace(/(?:chf|sfr\.?|fr\.?|eur|€)/gi, "")
      .replace(/\s/g, "")
      .replace(/['\u2019\u00b4]/g, "")
      .replace(/[^0-9.,-]/g, "");

    if (!s) return null;

    // The decimal separator is the rightmost "." or "," followed by 1-2 digits at
    // the end. Anything else (including "." or "," between thousands) is grouping.
    var value;
    var decMatch = s.match(/[.,](\d{1,2})$/);

    if (decMatch) {
      var decimalPart = decMatch[1];
      var integerPart = s.slice(0, s.length - decMatch[0].length).replace(/[.,]/g, "");
      value = parseFloat((integerPart || "0") + "." + decimalPart);
    } else {
      value = parseFloat(s.replace(/[.,]/g, ""));
    }

    return Number.isFinite(value) ? value : null;
  }

  function formatMoney(value) {
    var fixed = Number(value).toFixed(2); // e.g. "2500.00"
    var parts = fixed.split(".");
    var integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, MONEY_THOUSANDS);
    var number = integerPart + MONEY_DECIMAL + parts[1];

    var space = MONEY_SYMBOL_SPACE ? " " : "";

    return MONEY_SYMBOL_BEFORE
      ? MONEY_SYMBOL + space + number
      : number + space + MONEY_SYMBOL;
  }

  function centsToMoney(cents) {
    var value = Number(cents || 0) / 100;
    return Number.isFinite(value) ? value : 0;
  }

  function getTextNodes(root) {
    var result = [];

    if (!root) return result;

    var walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          var text = cleanText(node.nodeValue);

          if (!text) return NodeFilter.FILTER_REJECT;
          if (text.length > 120) return NodeFilter.FILTER_REJECT;

          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    var node;

    while ((node = walker.nextNode())) {
      result.push(node);
    }

    return result;
  }

  function textNodeMatchesLabel(node, labels, excludedLabels) {
    var text = lowerText(node.nodeValue);

    if (!text) return false;
    if (looksLikeMoney(text)) return false;
    if (isFreeText(text)) return false;

    if (excludedLabels && containsAny(text, excludedLabels)) {
      return false;
    }

    for (var i = 0; i < labels.length; i++) {
      var label = labels[i];

      if (text === label) return true;
      if (text.indexOf(label) === 0) return true;

      if (label.length >= 8 && text.indexOf(label) !== -1) {
        return true;
      }
    }

    return false;
  }

  function findLabelNode(root, labels, excludedLabels) {
    var nodes = getTextNodes(root);

    for (var i = 0; i < nodes.length; i++) {
      if (textNodeMatchesLabel(nodes[i], labels, excludedLabels)) {
        return nodes[i];
      }
    }

    return null;
  }

  function rowHasDifferentSummaryLabel(row, group) {
    var text = lowerText(elementText(row));

    if (group !== "shipping" && containsAny(text, SHIPPING_LABELS)) {
      return true;
    }

    if (group !== "total" && containsAny(text, TOTAL_LABELS)) {
      return true;
    }

    if (group !== "subtotal" && containsAny(text, SUBTOTAL_LABELS)) {
      return true;
    }

    return false;
  }

  function findSmallRowFromLabel(labelNode, group) {
    if (!labelNode || !labelNode.parentElement) return null;

    var current = labelNode.parentElement;
    var depth = 0;

    while (current && current !== document.body && depth < 10) {
      var text = elementText(current);

      if (
        text.length > 0 &&
        text.length <= 220 &&
        !rowHasDifferentSummaryLabel(current, group) &&
        (looksLikeMoney(text) || isFreeText(text))
      ) {
        return current;
      }

      current = current.parentElement;
      depth += 1;
    }

    return null;
  }

  function isKnownLabelText(text) {
    return (
      containsAny(text, SHIPPING_LABELS) ||
      containsAny(text, TOTAL_LABELS) ||
      containsAny(text, SUBTOTAL_LABELS)
    );
  }

  function findValueTextNodeAfterLabel(row, labelNode, allowFree) {
    if (!row || !labelNode) return null;

    var nodes = getTextNodes(row);
    var foundLabel = false;

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];

      if (node === labelNode) {
        foundLabel = true;
        continue;
      }

      if (!foundLabel) continue;

      var text = cleanText(node.nodeValue);

      if (!text) continue;

      if (isKnownLabelText(text)) {
        break;
      }

      if (looksLikeMoney(text) || (allowFree && isFreeText(text))) {
        return node;
      }
    }

    return null;
  }

  function findSummaryRootFromRow(row) {
    if (!row) return null;

    var current = row;
    var best = null;
    var depth = 0;

    while (current && current !== document.body && depth < 12) {
      var text = lowerText(elementText(current));

      if (
        text.length >= 40 &&
        text.length <= 3500 &&
        containsAny(text, SHIPPING_LABELS) &&
        containsAny(text, TOTAL_LABELS) &&
        containsAny(text, SUBTOTAL_LABELS)
      ) {
        best = current;
      }

      current = current.parentElement;
      depth += 1;
    }

    return best;
  }

  function findAllSummaryRoots() {
    var roots = [];
    var shippingLabels = [];

    var allTextNodes = getTextNodes(document.body);

    for (var i = 0; i < allTextNodes.length; i++) {
      if (textNodeMatchesLabel(allTextNodes[i], SHIPPING_LABELS, null)) {
        shippingLabels.push(allTextNodes[i]);
      }
    }

    for (var j = 0; j < shippingLabels.length; j++) {
      var row = findSmallRowFromLabel(shippingLabels[j], "shipping");
      var root = findSummaryRootFromRow(row);

      if (!root) continue;

      var exists = false;

      for (var k = 0; k < roots.length; k++) {
        if (roots[k] === root) {
          exists = true;
          break;
        }
      }

      if (!exists) {
        roots.push(root);
      }
    }

    return roots;
  }

  function findSummaryValue(root, group, labels, excludedLabels, allowFree) {
    var labelNode = findLabelNode(root, labels, excludedLabels);
    var row = findSmallRowFromLabel(labelNode, group);
    var valueNode = findValueTextNodeAfterLabel(row, labelNode, allowFree);

    return {
      labelNode: labelNode,
      row: row,
      valueNode: valueNode,
      labelText: labelNode ? cleanText(labelNode.nodeValue) : null,
      rowText: row ? elementText(row) : null,
      valueText: valueNode ? cleanText(valueNode.nodeValue) : null
    };
  }

  function rememberTextNode(node) {
    if (!node || node.__livomRemembered) return;

    node.__livomRemembered = true;
    node.__livomOriginalValue = node.nodeValue;

    var parent = node.parentElement;

    if (parent) {
      node.__livomOriginalInlineColor = parent.style.color || "";
    }

    patchedTextNodes.push(node);
  }

  function patchTextNode(node, newText) {
    if (!node) return false;

    rememberTextNode(node);

    if (cleanText(node.nodeValue) !== newText) {
      node.nodeValue = newText;
    }

    if (node.parentElement) {
      node.parentElement.style.setProperty("color", "inherit", "important");
    }

    return true;
  }

  function restorePatchedTextNodes() {
    for (var i = 0; i < patchedTextNodes.length; i++) {
      var node = patchedTextNodes[i];

      if (!node || !node.__livomRemembered) continue;

      if (node.isConnected) {
        node.nodeValue = node.__livomOriginalValue;

        if (node.parentElement) {
          node.parentElement.style.color = node.__livomOriginalInlineColor || "";
        }
      }

      node.__livomRemembered = false;
    }

    patchedTextNodes = [];
  }

  function itemMatchesRule(item, tags, rule) {
    // A rule with no conditions must not match everything.
    if (!rule.productType && !rule.tag && !rule.titleIncludes) {
      return false;
    }

    if (rule.productType) {
      var pt = String(item.product_type || "").trim().toLowerCase();
      if (pt !== String(rule.productType).toLowerCase()) return false;
    }

    if (rule.tag) {
      if (tags.indexOf(String(rule.tag).toLowerCase()) === -1) return false;
    }

    if (rule.titleIncludes) {
      var title = String(item.product_title || item.title || "").toLowerCase();
      if (title.indexOf(String(rule.titleIncludes).toLowerCase()) === -1) return false;
    }

    return true;
  }

  function matchRuleForItem(item, tags) {
    for (var i = 0; i < SHIPPING_RULES.length; i++) {
      if (itemMatchesRule(item, tags, SHIPPING_RULES[i])) {
        return SHIPPING_RULES[i];
      }
    }

    return null;
  }

  function normalizeTags(rawTags) {
    if (Array.isArray(rawTags)) {
      return rawTags.map(function (tag) {
        return String(tag).trim().toLowerCase();
      });
    }

    return String(rawTags || "")
      .split(",")
      .map(function (tag) {
        return String(tag).trim().toLowerCase();
      })
      .filter(Boolean);
  }

  // PERFORMANCE: every current rule matches on product_type, which /cart.js already
  // returns per line. The products/<handle>.js request (one per cart line, per refresh)
  // is only needed when a rule sets `tag`, so skip it otherwise.
  var RULES_NEED_TAGS = SHIPPING_RULES.some(function (rule) {
    return !!rule.tag;
  });

  function fetchProductTags(item) {
    if (!RULES_NEED_TAGS || !item || !item.handle) {
      return Promise.resolve([]);
    }

    var handle = String(item.handle);

    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) {
      return Promise.resolve(tagCache[handle]);
    }

    return fetchJson(shopifyUrl("products/" + encodeURIComponent(handle) + ".js"))
      .then(function (product) {
        var tags = normalizeTags(product.tags);

        tagCache[handle] = tags;

        log("Product tags fetched", {
          handle: handle,
          productType: item.product_type,
          tags: tags
        });

        return tags;
      })
      .catch(function (error) {
        log("Product tag fetch failed. Treating as no tags.", {
          handle: handle,
          error: error
        });

        tagCache[handle] = [];
        return [];
      });
  }

  function resolveCart(cart) {
    var items = Array.isArray(cart && cart.items) ? cart.items : [];

    lastCartBaseTotal = centsToMoney(cart.total_price);

    return Promise.all(
      items.map(function (item) {
        return fetchProductTags(item).then(function (tags) {
          return { item: item, tags: tags, rule: matchRuleForItem(item, tags) };
        });
      })
    ).then(function (rows) {
      // One charge per DISTINCT matched rule, summed. Mirrors Shopify charging
      // one rate per shipping profile that has items in the cart.
      var matchedAmounts = {};

      rows.forEach(function (row) {
        if (row.rule) {
          matchedAmounts[row.rule.key] = row.rule.amount;
        }
      });

      var keys = Object.keys(matchedAmounts);

      lastMatchedKeys = keys;
      lastTargetShipping =
        keys.length === 0
          ? null
          : keys.reduce(function (sum, k) {
              return sum + matchedAmounts[k];
            }, 0);

      log("Cart resolved", {
        cartBaseTotal: lastCartBaseTotal,
        matchedRules: keys,
        targetShipping: lastTargetShipping
      });

      return {
        matchedKeys: keys,
        targetShipping: lastTargetShipping
      };
    });
  }

  function applyPatch() {
    if (isCheckoutPage()) return;

    if (lastTargetShipping === null) {
      restorePatchedTextNodes();
      return;
    }

    var summaryRoots = findAllSummaryRoots();

    if (!summaryRoots.length) {
      log("No cart summary found yet.");
      return;
    }

    for (var i = 0; i < summaryRoots.length; i++) {
      var root = summaryRoots[i];

      var shipping = findSummaryValue(
        root,
        "shipping",
        SHIPPING_LABELS,
        null,
        true
      );

      var total = findSummaryValue(
        root,
        "total",
        TOTAL_LABELS,
        SUBTOTAL_LABELS,
        false
      );

      if (!shipping.valueNode || !total.valueNode) {
        log("Could not find exact summary text nodes. Skipping this summary.", {
          shipping: shipping,
          total: total
        });
        continue;
      }

      var currentShipping = parseMoney(shipping.valueText);
      var currentTotal = parseMoney(total.valueText);

      if (currentShipping === null || currentTotal === null) {
        log("Could not parse current amounts. Skipping this summary.", {
          shippingText: shipping.valueText,
          totalText: total.valueText
        });
        continue;
      }

      // Idempotent: shipping already shows the warehouse rate -> assume patched.
      if (Math.abs(currentShipping - lastTargetShipping) < 0.01) {
        continue;
      }

      // Discount-safe: keep Dr Stacked's own total (it already reflects any code)
      // and swap only the shipping component. This also sidesteps the
      // "Summe" vs "Zwischensumme" substring clash, since the subtotal line
      // is never read here.
      var correctedTotal = currentTotal - currentShipping + lastTargetShipping;

      patchTextNode(shipping.valueNode, formatMoney(lastTargetShipping));
      patchTextNode(total.valueNode, formatMoney(correctedTotal));

      log("Patched summary text nodes.", {
        oldShipping: shipping.valueText,
        newShipping: formatMoney(lastTargetShipping),
        oldTotal: total.valueText,
        newTotal: formatMoney(correctedTotal)
      });
    }
  }

  function refreshFromServer() {
    if (isCheckoutPage()) return Promise.resolve();

    return fetchJson(shopifyUrl("cart.js"))
      .then(resolveCart)
      .then(applyPatch)
      .catch(function (error) {
        log("Cart refresh failed. No patch applied.", error);
      });
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshFromServer, 250);
  }

  function scheduleApply() {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(applyPatch, 100);
  }

  function bindEvents() {
    [
      "cart:updated",
      "cart:refresh",
      "cart:change",
      "ajaxCart:updated",
      "cart:build",
      "cart:open",
      "dr:cart:open",
      "dr:cart:updated"
    ].forEach(function (eventName) {
      document.addEventListener(eventName, scheduleRefresh, {
        passive: true
      });
    });
  }

  function startDomObserver() {
    if (domObserver) return;

    // Re-apply the patch only when the app actually re-renders the drawer.
    // childList/subtree only: our own writes are text (characterData) and style
    // (attribute) changes, which this observer does NOT watch, so there is no
    // feedback loop. No network here -- cart data is refreshed by cart events.
    domObserver = new MutationObserver(function () {
      if (isCheckoutPage()) return;
      scheduleApply();
    });

    // Scoped to <main> (the cart page content). The overlay group (modals, WhatsApp
    // float) and app widgets appended to <body> no longer wake this up.
    domObserver.observe(document.getElementById("main") || document.body, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    if (isCheckoutPage()) {
      log("Checkout detected. Script inactive.");
      return;
    }

    bindEvents();

    // One read on load. The 500/1500/3000 ms re-reads existed for the cart DRAWER, which
    // rendered late; the cart page summary is server-rendered, the cart events above
    // trigger a re-read on every change and the observer re-applies after re-renders.
    refreshFromServer();

    startDomObserver();
  }

  window.LivomWarehouseShippingOverride = {
    refresh: refreshFromServer,
    apply: scheduleApply,
    restore: restorePatchedTextNodes,
    debug: function () {
      var roots = findAllSummaryRoots();

      return {
        checkoutPage: isCheckoutPage(),
        cartBaseTotal: lastCartBaseTotal,
        matchedRules: lastMatchedKeys,
        targetShipping: lastTargetShipping,
        summaryCount: roots.length,
        summaries: roots.map(function (root) {
          return {
            rootText: elementText(root),
            shipping: findSummaryValue(root, "shipping", SHIPPING_LABELS, null, true),
            total: findSummaryValue(root, "total", TOTAL_LABELS, SUBTOTAL_LABELS, false),
            subtotal: findSummaryValue(root, "subtotal", SUBTOTAL_LABELS, null, false)
          };
        }),
        tagCache: tagCache
      };
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, {
      once: true
    });
  } else {
    init();
  }
})();